import React, { useEffect, useState } from 'react';
import { ArrowLeft, FileText, Folder, Plus, Upload, Edit2, Trash2, X, MoreVertical, Copy, FilePenLine } from 'lucide-react';
import { Brand, MenuMetadata } from '../types';
import { getBrands, getMenus, createBrand, updateBrand, deleteBrand, duplicateMenu, deleteMenu, createMenu, updateMenuMetadata } from '../services/cloudflareService';

interface DashboardProps {
  onSelectMenu: (menuId: string, menuName: string) => void;
  onCreateMenu?: (brandId: string) => void;
  initialBrandId?: string | null;
  onBrandSelect?: (brandId: string | null) => void;
  refreshKey?: number;
}

export function Dashboard({ onSelectMenu, onCreateMenu, initialBrandId, onBrandSelect, refreshKey = 0 }: DashboardProps) {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [menus, setMenus] = useState<MenuMetadata[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCreatingBrand, setIsCreatingBrand] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');
  const [newBrandLogo, setNewBrandLogo] = useState<string | null>(null);

  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [deletingBrand, setDeletingBrand] = useState<Brand | null>(null);
  const [editName, setEditName] = useState('');
  const [editLogo, setEditLogo] = useState<string | null>(null);

  const [activeMenuDropdown, setActiveMenuDropdown] = useState<string | null>(null);
  const [deletingMenu, setDeletingMenu] = useState<MenuMetadata | null>(null);
  
  const [isCreatingMenu, setIsCreatingMenu] = useState(false);
  const [newMenuName, setNewMenuName] = useState('');

  const [editingMenu, setEditingMenu] = useState<MenuMetadata | null>(null);
  const [editMenuName, setEditMenuName] = useState('');
  const [editMenuLogo, setEditMenuLogo] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const data = await getBrands();
        setBrands(data);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const handleCreateBrand = () => {
    setIsCreatingBrand(true);
    setNewBrandName('');
    setNewBrandLogo(null);
  };

  const handleSaveNewBrand = async () => {
    if (!newBrandName.trim()) return;
    const newBrand = await createBrand(newBrandName, newBrandLogo);
    setBrands(prev => [...prev, newBrand]);
    setIsCreatingBrand(false);
  };

  const handleCancelCreate = () => {
    setIsCreatingBrand(false);
    setNewBrandName('');
    setNewBrandLogo(null);
  };

  const handleEditClick = (e: React.MouseEvent, brand: Brand) => {
    e.stopPropagation();
    setEditingBrand(brand);
    setEditName(brand.name);
    setEditLogo(brand.logo || null);
  };

  const handleDeleteClick = (e: React.MouseEvent, brand: Brand) => {
    e.stopPropagation();
    setDeletingBrand(brand);
  };

  const handleUpdateBrand = async () => {
    if (!editingBrand || !editName.trim()) return;
    await updateBrand(editingBrand.id, editName, editLogo);
    setBrands(prev => prev.map(b => b.id === editingBrand.id ? { ...b, name: editName, logo: editLogo || undefined } : b));
    setEditingBrand(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingBrand) return;
    await deleteBrand(deletingBrand.id);
    setBrands(prev => prev.filter(b => b.id !== deletingBrand.id));
    setDeletingBrand(null);
  };

  const handleEditLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditLogo(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewBrandLogo(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMenuDropdownClick = (e: React.MouseEvent, menuId: string) => {
    e.stopPropagation();
    setActiveMenuDropdown(activeMenuDropdown === menuId ? null : menuId);
  };

  const handleDuplicateMenu = async (e: React.MouseEvent, menuId: string) => {
    e.stopPropagation();
    setActiveMenuDropdown(null);
    setIsLoading(true);
    try {
        const newMenu = await duplicateMenu(menuId);
        if (newMenu) {
            setMenus(prev => [...prev, newMenu]);
        }
    } finally {
        setIsLoading(false);
    }
  };

  const handleDeleteMenuClick = (e: React.MouseEvent, menu: MenuMetadata) => {
    e.stopPropagation();
    setActiveMenuDropdown(null);
    setDeletingMenu(menu);
  };

  const handleConfirmDeleteMenu = async () => {
    if (!deletingMenu) return;
    await deleteMenu(deletingMenu.id);
    setMenus(prev => prev.filter(m => m.id !== deletingMenu.id));
    setDeletingMenu(null);
  };

  const handleCreateMenuClick = () => {
      setIsCreatingMenu(true);
      setNewMenuName('');
  };

  const handleSaveNewMenu = async () => {
      if (!selectedBrand || !newMenuName.trim()) return;
      const newMenu = await createMenu(selectedBrand.id, newMenuName);
      setMenus(prev => [...prev, newMenu]);
      setIsCreatingMenu(false);
  };

  const handleCancelCreateMenu = () => {
      setIsCreatingMenu(false);
      setNewMenuName('');
  };

  const handleEditMenuClick = (e: React.MouseEvent, menu: MenuMetadata) => {
      e.stopPropagation();
      setActiveMenuDropdown(null);
      setEditingMenu(menu);
      setEditMenuName(menu.name);
      setEditMenuLogo(menu.logo || null);
  };

  const handleUpdateMenu = async () => {
      if (!editingMenu || !editMenuName.trim()) return;
      await updateMenuMetadata(editingMenu.id, editMenuName, editMenuLogo);
      setMenus(prev => prev.map(m => m.id === editingMenu.id ? { ...m, name: editMenuName, logo: editMenuLogo || undefined } : m));
      setEditingMenu(null);
  };

  const handleEditMenuLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditMenuLogo(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveMenuDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!initialBrandId || !brands.length || selectedBrand) return;
    const found = brands.find((b) => b.id === initialBrandId);
    if (found) {
      handleBrandClick(found);
    }
  }, [initialBrandId, brands, selectedBrand]);

  useEffect(() => {
    if (!refreshKey || !selectedBrand) return;
    void loadMenus(selectedBrand.id);
  }, [refreshKey, selectedBrand]);

  const loadMenus = async (brandId: string) => {
    setIsLoading(true);
    try {
      const data = await getMenus(brandId);
      setMenus(data);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBrandClick = (brand: Brand) => {
    setSelectedBrand(brand);
    if (onBrandSelect) onBrandSelect(brand.id);
    void loadMenus(brand.id);
  };

  const handleBack = () => {
    setSelectedBrand(null);
    setMenus([]);
    if (onBrandSelect) onBrandSelect(null);
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6 md:p-10">
      <div className="max-w-6xl mx-auto space-y-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {selectedBrand && (
              <button
                onClick={handleBack}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Brands
              </button>
            )}
            <div>
              {!selectedBrand && <h1 className="text-2xl md:text-3xl font-black text-slate-900">Menu Manager</h1>}
              {selectedBrand && <h1 className="text-2xl md:text-3xl font-black text-slate-900">{selectedBrand.name}</h1>}
              <p className="text-sm text-slate-500">
                {selectedBrand ? 'Select a menu to work with' : 'Select a brand to manage its menus'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isLoading && (
                <span className="text-xs font-semibold text-slate-500">Loading…</span>
            )}
            {!selectedBrand && (
                <button 
                    onClick={handleCreateBrand}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" />
                    New Brand
                </button>
            )}
            {selectedBrand && (
                <button 
                    onClick={handleCreateMenuClick}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" />
                    New Menu
                </button>
            )}
          </div>
        </div>

        {isCreatingMenu && (
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Create New Menu</h2>
            <div className="flex items-center gap-4">
              <input
                type="text"
                placeholder="Enter Menu Name..."
                value={newMenuName}
                onChange={(e) => setNewMenuName(e.target.value)}
                className="flex-1 px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
              <button
                onClick={handleSaveNewMenu}
                disabled={!newMenuName.trim()}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
              >
                Create
              </button>
              <button
                onClick={handleCancelCreateMenu}
                className="px-6 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {isCreatingBrand && (
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Create New Brand</h2>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Enter Brand Name..."
                value={newBrandName}
                onChange={(e) => setNewBrandName(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
              <div className="flex items-center gap-4">
                <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors">
                  <Upload className="w-4 h-4" />
                  {newBrandLogo ? 'Change Logo' : 'Upload Brand Logo'}
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                </label>
                {newBrandLogo && (
                  <span className="text-sm text-green-600 font-medium">Logo selected</span>
                )}
              </div>
              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={handleSaveNewBrand}
                  disabled={!newBrandName.trim()}
                  className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
                >
                  Create
                </button>
                <button
                  onClick={handleCancelCreate}
                  className="px-6 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {!selectedBrand && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {brands.map((brand) => (
              <button
                key={brand.id}
                onClick={() => handleBrandClick(brand)}
                className="group flex flex-col items-start gap-4 p-6 rounded-xl border border-slate-200 bg-white hover:border-blue-500 hover:shadow-md transition-all h-48"
              >
                <div className="w-full h-24 flex items-center justify-center mb-2">
                    {brand.logo ? (
                        <img src={brand.logo} alt={brand.name} className="max-h-full max-w-full object-contain" />
                    ) : (
                        <div className="w-16 h-16 rounded-full bg-slate-50 flex items-center justify-center text-slate-400">
                            <Folder className="w-8 h-8" />
                        </div>
                    )}
                </div>
                <div className="text-left w-full border-t border-slate-100 pt-4 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-lg font-bold text-slate-900 group-hover:text-blue-700 truncate">{brand.name}</div>
                    <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => handleEditClick(e, brand)}
                        className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit Brand"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => handleDeleteClick(e, brand)}
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete Brand"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 truncate">Click to view menus</div>
                </div>
              </button>
            ))}
            {!brands.length && (
              <div className="col-span-full text-sm text-slate-500">No brands available.</div>
            )}
          </div>
        )}

        {selectedBrand && (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {menus.map((menu) => (
                <div
                  key={menu.id}
                  onClick={() => onSelectMenu(menu.id, menu.name)}
                  className="group relative flex flex-col bg-white rounded-xl border border-slate-200 hover:border-blue-500 hover:shadow-md transition-all cursor-pointer"
                >
                  {/* Header Image Area */}
                  <div className="h-32 bg-slate-50 relative flex items-center justify-center p-4 rounded-t-xl overflow-hidden">
                    {/* Status Badge */}
                    <div className="absolute top-3 left-3 z-10">
                        <span className={`px-2 py-1 rounded text-xs font-bold text-white ${menu.status === 'live' ? 'bg-green-500' : 'bg-slate-500'}`}>
                            {menu.status === 'live' ? 'Live' : 'Offline'}
                        </span>
                    </div>
                    
                    {/* Item Count Badge */}
                    <div className="absolute bottom-3 right-3 z-10">
                        <span className="px-2 py-1 rounded bg-slate-800/80 text-white text-xs font-bold">
                            {menu.itemCount || 0} Items
                        </span>
                    </div>

                    {/* Logo */}
                    {selectedBrand.logo ? (
                        <img src={selectedBrand.logo} alt={selectedBrand.name} className="max-h-full max-w-full object-contain" />
                    ) : (
                        <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-slate-400 shadow-sm">
                            <FileText className="w-6 h-6" />
                        </div>
                    )}
                  </div>

                  {/* Content Area */}
                  <div className="p-4 flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-slate-900 truncate group-hover:text-blue-700 text-lg">{menu.name}</h3>
                        <p className="text-xs text-slate-500 mt-1">
                            Last updated: {menu.updatedAt ? new Date(menu.updatedAt).toLocaleDateString() : 'Never'}
                        </p>
                    </div>
                    
                    {/* Dropdown Trigger */}
                    <div className="relative">
                        <button 
                            onClick={(e) => handleMenuDropdownClick(e, menu.id)}
                            className="p-1.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                        >
                            <MoreVertical className="w-5 h-5" />
                        </button>

                        {/* Dropdown Menu */}
                        {activeMenuDropdown === menu.id && (
                            <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-xl border border-slate-100 z-20 py-1 animate-in fade-in zoom-in-95 duration-100">
                                <button 
                                    onClick={(e) => handleEditMenuClick(e, menu)}
                                    className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                                >
                                    <FilePenLine className="w-4 h-4" />
                                    Edit Menu
                                </button>
                                <button 
                                    onClick={(e) => handleDuplicateMenu(e, menu.id)}
                                    className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                                >
                                    <Copy className="w-4 h-4" />
                                    Duplicate Menu
                                </button>
                                <div className="h-px bg-slate-100 my-1" />
                                <button 
                                    onClick={(e) => handleDeleteMenuClick(e, menu)}
                                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Delete Menu
                                </button>
                            </div>
                        )}
                    </div>
                  </div>
                </div>
              ))}
              {!menus.length && (
                <div className="col-span-full text-sm text-slate-500">No menus for this brand.</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Edit Brand Modal */}
      {editingBrand && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="text-lg font-bold text-slate-900">Edit Brand</h3>
              <button onClick={() => setEditingBrand(null)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Brand Logo</label>
                <div className="flex items-center gap-4">
                  {editLogo ? (
                    <div className="w-16 h-16 rounded-lg border border-slate-200 p-2 flex items-center justify-center bg-slate-50">
                      <img src={editLogo} alt="Preview" className="max-w-full max-h-full object-contain" />
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-lg border border-slate-200 flex items-center justify-center bg-slate-50 text-slate-400">
                      <Folder className="w-8 h-8" />
                    </div>
                  )}
                  <label className="cursor-pointer px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors text-sm font-medium">
                    Change Image
                    <input type="file" accept="image/*" onChange={handleEditLogoUpload} className="hidden" />
                  </label>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-slate-100 flex justify-end gap-3 bg-slate-50">
              <button
                onClick={() => setEditingBrand(null)}
                className="px-4 py-2 text-slate-700 font-medium hover:bg-slate-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateBrand}
                disabled={!editName.trim()}
                className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingBrand && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden">
            <div className="p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Delete Brand?</h3>
              <p className="text-slate-500 text-sm">
                Are you sure you want to delete <span className="font-semibold text-slate-900">{deletingBrand.name}</span>? This action cannot be undone and will remove all associated menus.
              </p>
            </div>
            <div className="p-4 border-t border-slate-100 flex gap-3 bg-slate-50">
              <button
                onClick={() => setDeletingBrand(null)}
                className="flex-1 px-4 py-2 text-slate-700 font-medium hover:bg-slate-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Edit Menu Modal */}
      {editingMenu && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="text-lg font-bold text-slate-900">Edit Menu</h3>
              <button onClick={() => setEditingMenu(null)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                <input
                  type="text"
                  value={editMenuName}
                  onChange={(e) => setEditMenuName(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Menu Cover Image</label>
                <div className="flex items-center gap-4">
                  {editMenuLogo ? (
                    <div className="w-16 h-16 rounded-lg border border-slate-200 p-2 flex items-center justify-center bg-slate-50">
                      <img src={editMenuLogo} alt="Preview" className="max-w-full max-h-full object-contain" />
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-lg border border-slate-200 flex items-center justify-center bg-slate-50 text-slate-400">
                      <FileText className="w-8 h-8" />
                    </div>
                  )}
                  <label className="cursor-pointer px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors text-sm font-medium">
                    Change Image
                    <input type="file" accept="image/*" onChange={handleEditMenuLogoUpload} className="hidden" />
                  </label>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-slate-100 flex justify-end gap-3 bg-slate-50">
              <button
                onClick={() => setEditingMenu(null)}
                className="px-4 py-2 text-slate-700 font-medium hover:bg-slate-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateMenu}
                disabled={!editMenuName.trim()}
                className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Menu Confirmation Modal */}
      {deletingMenu && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden">
            <div className="p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Delete Menu?</h3>
              <p className="text-slate-500 text-sm">
                Are you sure you want to delete <span className="font-semibold text-slate-900">{deletingMenu.name}</span>? This action cannot be undone.
              </p>
            </div>
            <div className="p-4 border-t border-slate-100 flex gap-3 bg-slate-50">
              <button
                onClick={() => setDeletingMenu(null)}
                className="flex-1 px-4 py-2 text-slate-700 font-medium hover:bg-slate-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeleteMenu}
                className="flex-1 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
